import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';

/**
 * Modal component for confirming Building Inspection deletion
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onDelete - Async callback to handle inspection deletion
 * @param {Object} props.inspection - Building Inspection object to be deleted
 * @returns {JSX.Element} Modal for confirming inspection deletion
 */
const DeleteBuildingInspectionConfirmationModal = ({
  open,
  onClose,
  onDelete,
  inspection,
}) => {
  const [loading, setLoading] = useState(false);

  /**
   * Handles the confirmation of building inspection deletion
   */
  const handleConfirm = async () => {
    try {
      setLoading(true);
      if (onDelete) {
        await onDelete();
      }
      onClose();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format the inspection description for better readability
  const inspectionDate = inspection?.inspection_date
    ? new Date(inspection.inspection_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'this inspection';

  const buildingName = inspection?.property_details?.building_name || 'unknown property';

  const completedBy = inspection?.completed_by?.completed_by_name || 'Unknown';

  return (
    <Modal isOpen={open} toggle={onClose} centered className="building-inspection-delete-modal">
      <ModalHeader toggle={onClose}>
        <FeatherIcon icon="alert-triangle" size={20} className="me-2 text-danger" />
        Confirm Deletion
      </ModalHeader>
      <ModalBody>
        <div className="text-center py-3">
          <FeatherIcon icon="home" size={48} className="text-danger mb-3" style={{ opacity: 0.7 }} />
          <p className="mb-3 lead">
            Are you sure you want to <strong>permanently delete</strong> the building inspection for:
          </p>
          <div className="bg-light p-3 rounded mb-4">
            <p className="mb-1">
              <strong>Property:</strong> {buildingName}
            </p>
            <p className="mb-1">
              <strong>Inspection Date:</strong> {inspectionDate}
            </p>
            <p className="mb-0">
              <strong>Completed By:</strong> {completedBy}
            </p>
          </div>
          <p className="text-muted">
            <FeatherIcon icon="alert-circle" size={16} className="me-1" />
            <strong>This action cannot be undone.</strong> All data, comments, photos, and recommendation notes will be permanently removed.
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          outline
          color="secondary"
          onClick={onClose}
          disabled={loading}
          size="lg"
        >
          Cancel
        </Button>
        <Button
          color="danger"
          onClick={handleConfirm}
          disabled={loading}
          size="lg"
          className="px-4"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Deleting...
            </>
          ) : (
            <>
              <FeatherIcon icon="trash-2" size={16} className="me-2" />
              Delete Inspection
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export { DeleteBuildingInspectionConfirmationModal };